import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';
import { CU } from 'common_library';
import { getValidationConfigFromCardNo } from 'src/app/my-formly/components/credit-card/card.helper';

/**
 * Anteprima grafica della carta che si compone mentre l'utente digita.
 * Usata sia nel form di registrazione (dati live) sia nel recap della carta salvata
 * (numero mascherato + circuito noto via [brand]).
 */
@Component({
  selector: 'yf-card-preview',
  templateUrl: './card-preview.component.html',
  styleUrls: ['./card-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CardPreviewComponent {
  $number = signal<string>('');
  $owner = signal<string>('');
  $expiryMonth = signal<string>('');
  $expiryYear = signal<string>('');
  $cvv = signal<string>('');
  $flipped = signal<boolean>(false);
  $brandOverride = signal<string>('');

  @Input() set number(v: string | null | undefined) { this.$number.set(v ?? ''); }
  @Input() set owner(v: string | null | undefined) { this.$owner.set(v ?? ''); }
  @Input() set expiryMonth(v: string | number | null | undefined) {
    this.$expiryMonth.set(v || v === 0 ? `${v}`.padStart(2, '0') : '');
  }
  @Input() set expiryYear(v: string | number | null | undefined) {
    this.$expiryYear.set(v ? `${v}`.slice(-2) : '');
  }
  @Input() set cvv(v: string | number | null | undefined) { this.$cvv.set(v != null ? `${v}` : ''); }
  /** true = mostra il retro (CVV) */
  @Input() set flipped(v: boolean) { this.$flipped.set(!!v); }
  /** circuito noto a priori (carta salvata): scavalca la detection dal numero */
  @Input() set brand(v: string | null | undefined) { this.$brandOverride.set(v ?? ''); }

  /** numero in gruppi di 4; le cifre mancanti (o i caratteri di mascheramento) diventano '•' */
  $groups = computed(() => {
    let s = this.$number().replace(/\s+/g, '').replace(/[^\d]/g, '•');
    if (s.length < 16) s = s.padEnd(16, '•');
    return s.match(/.{1,4}/g) ?? [];
  });

  $brandType = computed(() => {
    if (this.$brandOverride()) return this.$brandOverride();
    const digits = this.$number().replace(/[^\d]/g, '');
    return digits.length >= 2 ? getValidationConfigFromCardNo(digits)?.type ?? '' : '';
  });

  $brandLogo = computed(() => (this.$brandType() ? CU.getLogoByCdcType(this.$brandType()) : ''));

  $expiry = computed(() => {
    const m = this.$expiryMonth();
    const y = this.$expiryYear();
    return m || y ? `${m || '••'}/${y || '••'}` : '••/••';
  });

  $cvvDisplay = computed(() => this.$cvv() || '•••');
}
